import type { EventName, EventPayloadMap } from './event-types';

type Handler<TName extends EventName> = (payload: EventPayloadMap[TName]) => void;

/**
 * Lightweight in-process event bus per docs/architecture/ARCHITECTURE-V2-ADDENDUM.md (TASK 3)
 * and ADR-005. No message broker, no persistence, no cross-process delivery —
 * intentionally simple, fire-and-forget, type-safe.
 */
export class EventBus {
  private readonly handlers: Map<EventName, Set<Handler<EventName>>> = new Map();

  public subscribe<TName extends EventName>(name: TName, handler: Handler<TName>): () => void {
    const existing = this.handlers.get(name) ?? new Set<Handler<EventName>>();
    existing.add(handler as Handler<EventName>);
    this.handlers.set(name, existing);

    return () => {
      existing.delete(handler as Handler<EventName>);
    };
  }

  public publish<TName extends EventName>(name: TName, payload: EventPayloadMap[TName]): void {
    const subscribers = this.handlers.get(name);
    if (!subscribers || subscribers.size === 0) {
      return;
    }
    for (const handler of subscribers) {
      // Per ADR-005: publishers never know about subscribers' outcomes (fire-and-forget).
      // A handler failure must not break the publisher or other handlers.
      try {
        (handler as Handler<TName>)(payload);
      } catch {
        // Intentionally swallowed here; the handler itself is responsible for
        // logging its own failures via the Logging infrastructure.
      }
    }
  }
}
